// Shared query builder utility for constructing URL search parameters
// Used across all API domain services for consistent query string construction

/**
 * Generic query parameters that can be used across different endpoints
 */
export interface QueryParams {
  page?: number;
  pageSize?: number;
  searchText?: string | null;
  orderBy?: string | null;
  orderDirection?: string | null;
  [key: string]: string | number | boolean | null | undefined | string[];
}

/**
 * Build a URL query string from parameters object.
 * Automatically filters out null, undefined, and empty string values.
 * Converts arrays to comma-separated strings.
 * Returns empty string if no valid parameters.
 *
 * @param params - Object with query parameters
 * @param options - Configuration options
 * @param options.includeQuestionMark - Whether to prefix with '?' (default: true)
 * @param options.requiredParams - List of parameter keys that must be included even if empty
 * @returns Query string (e.g., '?page=1&pageSize=10' or '')
 *
 * @example
 * ```ts
 * buildQueryString({ page: 1, pageSize: 10, searchText: 'test' })
 * // Returns: '?page=1&pageSize=10&searchText=test'
 *
 * buildQueryString({ page: 1, searchText: null })
 * // Returns: '?page=1'
 *
 * buildQueryString({ companyIds: [1, 2, 3] })
 * // Returns: '?companyIds=1,2,3'
 * ```
 */
export function buildQueryString(
  params: QueryParams,
  options: {
    includeQuestionMark?: boolean;
    requiredParams?: string[];
  } = {},
): string {
  const { includeQuestionMark = true, requiredParams = [] } = options;
  const queryParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    // Skip null, undefined, and empty strings unless required
    if (value === null || value === undefined) {
      if (requiredParams.includes(key)) {
        continue; // Skip even if required - null/undefined means not set
      }
      continue;
    }

    // Handle empty strings - only add if required
    if (value === '' && !requiredParams.includes(key)) {
      continue;
    }

    // Handle arrays - join with comma
    if (Array.isArray(value)) {
      if (value.length > 0) {
        queryParams.set(key, value.join(','));
      }
      continue;
    }

    // Handle booleans and numbers - always convert to string
    if (typeof value === 'boolean' || typeof value === 'number') {
      queryParams.set(key, String(value));
      continue;
    }

    // Handle strings
    if (typeof value === 'string') {
      queryParams.set(key, value);
    }
  }

  const queryString = queryParams.toString();
  if (!queryString) {
    return '';
  }

  return includeQuestionMark ? `?${queryString}` : queryString;
}

/**
 * Helper to build query string with required parameters.
 * Throws error if any required parameter is missing or invalid.
 *
 * @param params - Object with query parameters
 * @param requiredKeys - Array of keys that must be present and valid
 * @param options - Same options as buildQueryString
 * @returns Query string
 * @throws Error if required parameter is missing
 *
 * @example
 * ```ts
 * buildRequiredQueryString(
 *   { startDate: '2024-01-01', endDate: '2024-12-31' },
 *   ['startDate', 'endDate']
 * )
 * // Returns: '?startDate=2024-01-01&endDate=2024-12-31'
 *
 * buildRequiredQueryString({ startDate: '2024-01-01' }, ['startDate', 'endDate'])
 * // Throws: Error: Required query parameter 'endDate' is missing
 * ```
 */
export function buildRequiredQueryString(
  params: QueryParams,
  requiredKeys: string[],
  options?: { includeQuestionMark?: boolean },
): string {
  // Validate required parameters
  for (const key of requiredKeys) {
    const value = params[key];
    if (value === null || value === undefined || value === '') {
      throw new Error(`Required query parameter '${key}' is missing`);
    }
    if (Array.isArray(value) && value.length === 0) {
      throw new Error(`Required query parameter '${key}' is empty array`);
    }
  }

  return buildQueryString(params, {
    ...options,
    requiredParams: requiredKeys,
  });
}
